import * as React from "react";
import { GetFaQsByEventQuery } from "@src/@types/api.d";
import { useFaqList } from "@src/hooks/useFaqList";
import { motion, AnimatePresence } from "framer-motion";

export type ITemplate12FAQ = {
  eventId: number;
  faqData: GetFaQsByEventQuery;
  loading?: boolean;
};

const BG_COLOR = "bg-[#AC5E23]";
const CARD_BG = "bg-[#FCF8EB]";
const CARD_BORDER = "border-[#AC5E23]/20";
const BUTTON_TEXT = "text-[#AC5E23]";
const TITLE_TEXT = "text-[#FCF8EB]";
const ACCENT_COLOR = "#AC5E23";

const FAQ: React.FC<ITemplate12FAQ> = ({ eventId, faqData }) => {
  const {
    faqItems,
    paginatedFaqItems,
    currentPage,
    totalPages,
    openIndex,
    toggleFAQ,
    handlePageChange,
  } = useFaqList({
    faqData,
    itemsPerPage: 3,
    resetOpenOnPageChange: true,
  });

  if (!faqItems.length) return null;

  const simulatedFaqItems =
    faqItems.length < 10
      ? [
          ...faqItems,
          ...Array.from({ length: 12 }, (_, i) => ({
            id: `demo-${i}`,
            question: `Sample Question ${i + 1}?`,
            answer: `This is a sample answer for demonstration purposes. In production, this will be replaced with real FAQ data from your backend.`,
            order_index: faqItems.length + i,
            event_id: eventId,
            event_part_id: null,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })),
        ]
      : faqItems;

  return (
    <div
      className={`w-full min-h-[60vh] py-16 lg:py-20 overflow-hidden relative ${BG_COLOR} flex flex-col`}
    >
      <div className="text-center mb-12 relative z-10">
        <h2
          className={`${TITLE_TEXT} font-caudex text-3xl md:text-4xl lg:text-5xl font-normal leading-tight tracking-wide`}
        >
          Q & A
        </h2>
      </div>

      <div className="max-w-4xl mx-auto w-full px-8 lg:px-12 flex flex-col flex-grow">
        <div className="flex flex-col gap-4 md:gap-5 flex-grow min-h-[220px] md:min-h-[300px]">
          {paginatedFaqItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className={`${CARD_BG} rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className={`w-full text-left p-5 md:p-6 lg:p-7 flex items-center justify-between gap-4 cursor-pointer transition-colors duration-300 hover:bg-white/50`}
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <h3
                  className={`${BUTTON_TEXT} font-caudex text-base md:text-lg lg:text-xl font-semibold leading-tight tracking-wide flex-1`}
                >
                  {item.question}
                </h3>

                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <svg
                    className="w-5 h-5 md:w-6 md:h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke={ACCENT_COLOR}
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </motion.div>
              </button>

              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    id={`faq-answer-${index}`}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 md:px-6 lg:px-7 pb-5 md:pb-6 lg:pb-7 pt-0">
                      <div className={`border-t ${CARD_BORDER} pt-4 md:pt-5`}>
                        <p
                          className={`${BUTTON_TEXT} font-caudex text-sm md:text-base lg:text-lg leading-relaxed whitespace-pre-line`}
                        >
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 md:gap-3 mt-auto pt-8 md:pt-12 relative z-15">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 md:px-5 md:py-2.5 rounded-lg ${CARD_BG} ${BUTTON_TEXT} border-2`}
              style={{ borderColor: ACCENT_COLOR }}
              aria-label="Previous page"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            <button
              onClick={() =>
                handlePageChange(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 md:px-5 md:py-2.5 rounded-lg ${CARD_BG} ${BUTTON_TEXT} border-2`}
              style={{ borderColor: ACCENT_COLOR }}
              aria-label="Next page"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQ;
